/**
 * `/` — the Overview: which parts of Wend this workspace has, and the Connect
 * dashboard under them.
 *
 * Slice 0 left this screen a placeholder and said why: *"inventing a dashboard
 * out of numbers that do not exist is how a demo starts lying."* Slice 10 built
 * the numbers and quoted that sentence back as the rule governing what may
 * appear here. **Slice 20 finally draws the charts, and it was deferred to last
 * for the same sentence**: built before there was real data, every gauge reads
 * unmeasured and every chart is flat, and a good design cannot be told from a
 * bad one.
 *
 * Everything below is the rules that survived that, restated because they are
 * what the file is shaped by. None of them relaxed to make a chart easier.
 *
 * ## 1. WHAT IS ON THE PAGE IS DECIDED BY ONE TEST: DOES THE NUMBER CHANGE A
 * DECISION?
 *
 * Contact counts, template counts, segment counts and campaigns-ever are cheap,
 * true, and change nothing, so none of them is here. A screen that is 70%
 * inventory teaches the reader to stop looking at it, which is expensive on the
 * one day it has something urgent to say. **A dashboard is not a licence to add
 * tiles**, and this slice — whose brief was "graphs and figures of KPIs" — is
 * exactly where that licence would have been assumed. What it added instead is
 * two charts and one block, each answering a question a person actually asks:
 * *am I sending*, *is anything going wrong*, *has anything stopped*.
 *
 * ## 2. THE ORDER IS A DECISION, NOT A PREFERENCE
 *
 * A halted campaign leads, because slice 18 made a campaign able to stop its own
 * send over how its messages were being received, and a half-sent list with
 * nobody told is the most urgent thing this screen can say. It rides on the
 * activity read, so when that read fails the same slot says the check could not
 * be made — silence there would be indistinguishable from "nothing stopped".
 *
 * **Slice 32 put "Worth doing next" second and "Coming up" third, and those are
 * the two places in this order where something that is not a figure outranks
 * one.** Every other card
 * on this page reports on sends that have already gone; that one is about a
 * send that has not. It sits under the halted-campaign slot because a
 * suggestion must never outrank an emergency, and over the rates because a
 * bounce rate is a fact to check rather than a thing to do. It renders nothing
 * at all when there is nothing to suggest, so on a well-run workspace the
 * figures are still what the page opens with.
 *
 * Under those three come the figures, and first among them the bounce and
 * complaint rates, because the worst *ongoing* thing that can happen in this
 * product is a tenant getting their own AWS account put under review — under
 * bring-your-own-SES that lands on the customer, and they hear it from Amazon
 * rather than from us unless this screen tells them first. **Then the last few
 * campaigns**, then what has been sent over thirty days beside what to do next,
 * then whether this workspace can send at all, then what came back, then the
 * suppression trend, and last of all engagement.
 *
 * ## SLICE 25 RE-RANKED IT, AND MOVED TWO ANSWERS OFF THE STACK ENTIRELY
 *
 * The order above is not what slice 20 wrote, and the differences are the whole
 * of slice 25. `wend-designer` measured this page at 1,280 x 800 against the
 * live workspace's own shape: **5.84 viewports, 1,489 words, and nothing above
 * the fold that is a figure.** Credits sat 3.37 viewports down and the most
 * recent campaign 4.13, behind four cards whose entire subject is why figures
 * are missing.
 *
 * **Recent campaigns moved seven places up**, to directly under the status row.
 * "What did my last campaign do" is asked more often than any of those
 * explanations, and a card two scrolls away is a card nobody reads.
 *
 * **Credits left the stack for the header.** It is the figure a sender checks
 * before every campaign and it was below everything. `AppLayout`'s `actions`
 * slot took nothing from this page until then; it now carries `CreditsChip`,
 * and `/credits` already rendered every figure the card printed.
 *
 * **And so did the sendability answer, which is the interesting one**, because
 * the paragraph this replaces was a compromise that had stopped holding. It
 * said: *"can this workspace send" is the third question the brief names as
 * above-the-fold, and its card is not — but its signal is*, the signal being
 * `QuickActions`' primary button. That works only while something is **broken**.
 * A healthy workspace's top tile says "New campaign", which answers what to do
 * next rather than whether sending works; and slice 25 demoted the sandbox out
 * of that slot, because an account that is sending, capped, was being drawn as
 * a repair over the reader's own job. So the compromise leaked in both
 * directions, and `SendabilityChip` sits in the header beside credits instead —
 * one chip per connected channel, looked up in the same table the sendability
 * card draws its badges from, so the two cannot come to disagree.
 *
 * `QuickActions` keeps the repair tiles and keeps its primary button for the
 * states that really are stops: "Connect Amazon SES", "The SES connection test
 * failed", "Amazon has paused sending", "No verified sender", Meta's verdict on
 * the number. Those still link to the card itself under the `sendability`
 * anchor, so a broken workspace still sees the problem above the fold and the
 * detail one scroll down. The first version put only the Wend-side repairs
 * there, so a paused account read as healthy above the fold; review found it,
 * and that is still true.
 *
 * **Slice 32 moved that fold back down, and this is the cost of the re-rank.**
 * Slice 25 measured the fold as falling after recent campaigns. On a workspace
 * with something to suggest, "Worth doing next" and "Coming up" now sit between
 * the status row and the figures, and measured at 1280x800 the calendar card
 * alone is 603px, so `SendingHealthTiles` — the bounce and complaint rates this
 * header calls the numbers that can cost somebody their AWS account — starts
 * around 1.8 viewports down.
 *
 * That is paid knowingly rather than overlooked. Both cards render **nothing**
 * when they have nothing to say: no country set, or no moment inside sixty days,
 * or nothing worth suggesting. A well-run workspace gets the fold straight back.
 * And the two answers slice 25 moved off this stack — credits and sendability —
 * are in the header at every scroll position regardless.
 *
 * ## SLICE 65: THE DASHBOARD IS CONNECT'S, AND A STRIP OF PILLARS SITS ABOVE IT
 *
 * Slice 65 split the old Reach in two: **Reach** is paid ads on the
 * workspace's own Meta ad account, and **Connect** is email and WhatsApp — what
 * "Reach" had meant on this page since slice 0. Every figure below is a message
 * Wend handed to SES or Meta's Cloud API, so the dashboard is Connect's, and
 * the gate moved with the meaning: a workspace whose plan has no Connect gets
 * `PillarSection feature="connect"` in its place, exactly as one without Reach
 * did before. Migration 0048 gave every Reach workspace Connect, so nobody who
 * had this dashboard lost it.
 *
 * Above both branches sits `PillarStrip` — a card per pillar this plan has
 * (Reach, Connect, Sense; never Flow, slice 26, and never one off the plan,
 * the sidebar's rule), each linking to where it starts. It is in both
 * branches because the workspace that most needs it is the one the dashboard
 * is not for: an ads-only plan otherwise lands on "not on this plan" with no
 * word about the pillar it does have. It draws nothing for Connect alone over
 * Connect's own dashboard. Its argument, and why it carries no figures, is in
 * its own header.
 *
 * **The header's actions are Connect's too, so they follow the same gate.**
 * The sendability chip, the credits chip, "Campaign settings" and the primary
 * "New campaign" are all about sending. Review found an ads-only workspace
 * shown all four — its one primary button belonging to a pillar its plan does
 * not have — directly above the section saying "there is nothing to send
 * with". Without Connect the header carries no actions: the strip already
 * links to the pillar the workspace does have, and a Reach button up here
 * would be a second way to the same place.
 *
 * **The rejected alternative was a dashboard per pillar on this page** — ad
 * spend beside the bounce rate, the Today queue beside the calendar. Each
 * pillar's screen already draws its figures with their own caveats, the
 * owner approved this page's layout as it stands, and two pillars' figures in
 * one grid invite a comparison this product refuses elsewhere: an ad's
 * figures are Meta's aggregates over people Wend never sees, a message's are a
 * receipt per person. The strip says where those are; it does not copy them.
 *
 * ## THE OVERVIEW IS ONE CARD FAMILY, AND ITS CHROME IS TIGHTER THAN THE APP'S
 *
 * Every card on this page — the rail's two, the three in the main column, and
 * every full-width card below the grid — is drawn with 18px of section padding
 * and a 0.95rem title, against the `Card` default of 24px and 1.15rem.
 *
 * **The default is right, and it is right for everywhere else.** On a page of
 * two or three cards, 24px and a 1.15rem heading are what a card should be. The
 * Overview is eleven cards and a rail 310px wide, and at that density the
 * default reads as furniture: the heading competes with the figures under it,
 * and a 24px card standing next to an 18px one reads as two different products
 * rather than one page.
 *
 * **So it is written at each call site, not made the default in `Card`.** The
 * default sits under every card in `apps/web` and every card in `apps/admin`, a
 * staff console nobody asked to redesign. Changing it would retheme all of them
 * to convert these — and, the part that decides it, no suite in this repo
 * asserts a padding or a type size, so it would do that silently. Slice 33
 * shipped this page in two card families and every suite read green for a whole
 * slice; `verify-dashboard` now has probes over `routes/overview` so a card
 * added on the default goes red where a reader would otherwise have had to
 * notice it.
 *
 * **Three cards are deliberately not in the family, and each for a reason that
 * is not padding.** `AttentionBlock` keeps its own larger heading because it is
 * the emergency slot and is meant to outweigh a title (it takes the 18px inset
 * only, so its left edge still lines up). `QuickActions` has an eyebrow rather
 * than a title, and is `h-full` against the chart beside it, so its inset moves
 * with that chart or not at all. `PillarSection` is returned INSTEAD of this
 * whole dashboard for a workspace with no Connect entitlement — it is a peer of
 * nothing here, and it stays on the default. `PillarStrip` is in the family:
 * its cards are short because they say little, not because they are another
 * kind of card.
 *
 * **There is no number in this paragraph on purpose.** The sentence it replaces
 * said "46 other call sites", was pasted onto two cards, and was wrong on both
 * by 66 before anybody re-counted. A justification needs a reason, not a census.
 *
 * ## 3. A RATE IS NEVER A BARE PERCENTAGE
 *
 * Every rate on this page is drawn against the AWS threshold it is measured
 * against. 4% means nothing; "4%, and Amazon reviews accounts above 5%" is a
 * decision. On a tile that is a mark on a bar; **on a chart it is a dashed line,
 * labelled** — and, because a daily rate would be noise, the line is drawn in
 * *messages*: how many of that day's accepted messages would have been 5%. It
 * moves with the day's volume, and it is drawn only on a day that clears the
 * rate floor with a report back: below the floor a line is a rate drawn, which
 * this rule forbids as surely as a printed one.
 *
 * ## 4. BELOW `OVERVIEW_MIN_VOLUME_FOR_RATE` ACCEPTED MESSAGES THERE IS NO
 * PERCENTAGE AT ALL
 *
 * Not greyed, not approximate — the counts, and a sentence saying why. A bounce
 * rate over forty messages is noise wearing a percentage sign. The floor is a
 * shared constant so the API and the screen cannot disagree about where it sits,
 * and the contract enforces it by leaving `rate` as `null` rather than computing
 * a number and flagging it. **The charts do not weaken this**: there is no
 * per-day rate on the wire and nowhere on `overview-activity.ts` to put one.
 *
 * ## 5. "NOTHING SENT YET" AND "0% BOUNCE RATE" ARE DIFFERENT STATEMENTS AND
 * ONLY ONE IS EVER TRUE
 *
 * This is the rule the charts made hard. A chart interpolates: given thirty
 * absent days it draws a confident flat line along the baseline, and that line
 * is what a healthy quiet week, an unconnected channel and a workspace that has
 * never sent all look like. The defence is three shapes rather than three
 * intentions — a channel with nothing to draw carries no bucket array, a day
 * before the workspace existed carries no count field, and `chartAxisMax`
 * returns `null` rather than a scale. `ActivityCharts.tsx` argues it at length.
 *
 * **And a fourth, found by proving this page against the deployment that
 * sends.** There, SNS is not wired and no delivery event has ever arrived, so
 * over a thousand accepted emails had every outcome count at `0` — and the
 * parked build printed "0.00% — Under the line" in green, drew a month of zero
 * bounce stubs under Amazon's line, and listed five reasons for non-delivery at
 * `0` each. Accepted-and-nothing-reported is not a zero. `noOutcomeReported` in
 * the overview contract names it, the tiles, the not-delivered card and both
 * outcome charts ask it before drawing an outcome, and the daily mappers answer
 * `null` for an outcome on a day nobody has reported on.
 *
 * ## 6. AN ENGAGEMENT NUMBER APPEARS ONLY FOR A WORKSPACE WITH ENGAGEMENT ON
 *
 * Every other workspace gets one sentence and **no number** — not a zero, not a
 * dash, not a greyed tile. The enforcement is structural:
 * `engagementPanelSchema`'s untracked arm has no field a number could live in.
 * Slice 20 did not weaken that to make a chart easier — there is no daily
 * engagement series at all, and `overview-activity.ts` has no `opened` and no
 * `clicked` field for one to arrive in. Engagement is present and **visually
 * subordinate**: no colour, no verdict badge, no threshold, last card on the
 * page. Opens and clicks are interesting; bounce and complaint rates are what
 * cost somebody their AWS account.
 *
 * ## 7. WHATSAPP READ RECEIPTS STAY OFF THIS SCREEN
 *
 * They have existed since slice 12 and are on the campaign's own results page,
 * drawn as a floor with both of their caveats. This page is where *sending
 * health* is read, and a read floor has no threshold, no verdict and no
 * direction anybody can act on.
 *
 * ## 8. EVERY PROVIDER FACT IS A STORED SNAPSHOT WITH ITS AGE ON IT
 *
 * The dashboard request calls neither AWS nor Meta, and neither does the
 * activity request — every count in that one comes from `messages` and
 * `delivery_events` in this database. Only the engagement request reaches
 * Amazon, which is the whole reason it is separate: an SESv2 metrics outage must
 * not take down the screen that reports the two rates which can cost somebody
 * their AWS account.
 *
 * ## FIVE READS, FIVE MOMENTS, AND EACH CARD STAMPS ITS OWN
 *
 * `GET /overview` is one snapshot and its figures may be compared with each
 * other. `GET /overview/activity` and `GET /overview/engagement` are their own
 * requests with their own timestamps and their own failure states. Each can fail
 * alone and none can take the others down. The one thing guaranteed across the
 * first two is that their windows are computed from the same constant by the
 * same function, so the bars and the tiles describe the same thirty days even
 * when they were read a second apart.
 *
 * ## WHAT THIS FILE STOPPED BEING
 *
 * It was 2,204 lines, and roughly nine tenths of that was prose carrying context
 * forward for whoever picked the next slice up. **That job is done.** Slice 20
 * applied the text rule: builder-reasoning is deleted from the UI and lives in a
 * file header; honesty about a number stays visible in four words rather than a
 * paragraph; instruction is progressive — short by default, detail on demand,
 * and *progressive does not mean deleted*, which slice 16 once got backwards.
 *
 * The cards moved to `routes/overview/` one file each, which is the other half
 * of the same problem: 2,204 lines in one route is why nobody could tell what
 * was on the page. This file is now the five reads, the order, and the layout —
 * and each of those is a decision, so each of them is argued above.
 *
 * ## SLICE 15'S CHECKLIST SURVIVES, AND IT IS NOT QUICK ACTIONS
 *
 * `OnboardingPanel` is mounted above everything, unconditionally, outside the
 * `data !== null` branch. It owns its own read and its own entitlement gate,
 * draws the five-step first-run checklist for a workspace that has not finished
 * setting up, and renders **nothing at all** for one that has — which is most
 * workspaces most of the time. A workspace that has connected nothing is exactly
 * the one whose dashboard has least to say, so the checklist must not wait on,
 * or be taken down by, the request that draws it. `QuickActions` is the other
 * thing: the checklist is about *setting up*, and those tiles are about *the
 * next thing to do today*.
 */
import {
  type CalendarResponse,
  type OpportunitiesResponse,
  type OverviewActivityResponse,
  type EngagementResponse,
  type ReachLedger,
  type OverviewResponse,
  type TemplateListResponse,
} from '@wend/shared';
import { Card, CardContent, CardHeader, CardTitle, Spinner, buttonVariants } from '@wend/ui';

import { Link } from 'react-router-dom';

import { listTemplates } from '../lib/api/templates';
import { AppLayout } from '../components/AppLayout';
import { ErrorState } from '../components/ErrorState';
import {
  fetchCalendar,
  fetchEngagement,
  fetchReach,
  fetchOpportunities,
  fetchOverview,
  fetchOverviewActivity,
} from '../lib/api/overview';
import { formatDate, formatDateTime } from '../lib/format';
import { useAsyncData, type AsyncData } from '../lib/use-async-data';
import { OnboardingPanel } from './onboarding/OnboardingPanel';
import { RecentWindowPanel } from './overview/ActivityCharts';
import { AttentionSlot } from './overview/AttentionBlock';
import { CreditsChip } from './overview/CreditsChip';
import { ReachLedgerCard } from './overview/ReachLedgerCard';
import { EngagementTiles } from './overview/EngagementTiles';
import { SendabilityChip } from './overview/SendabilityChip';
import { CalendarCard, MomentsCard } from './overview/CalendarCard';
import { TEMPLATE_ROWS, TemplatesCard } from './overview/TemplatesCard';
import { OpportunitiesCard } from './overview/OpportunitiesCard';
import { PillarSection } from './overview/PillarSection';
import { PillarStrip, pillarStateOf } from './overview/PillarStrip';
import { UnfinishedCampaignsCard } from './overview/UnfinishedCampaignsCard';
import { NotDeliveredCard, SendingHealthTiles } from './overview/SendingHealthCard';

/* -------------------------------------------------------------------------- */
/* The dashboard                                                               */
/* -------------------------------------------------------------------------- */

interface Reads {
  readonly overview: OverviewResponse;
  readonly activity: AsyncData<OverviewActivityResponse>;
  readonly opportunities: AsyncData<OpportunitiesResponse>;
  readonly calendar: AsyncData<CalendarResponse>;
  readonly engagement: AsyncData<EngagementResponse>;
  readonly reach: AsyncData<ReachLedger>;
  readonly templates: AsyncData<TemplateListResponse>;
}

/** What `Overview` hands the dashboard on top of the reads. */
interface DashboardProps extends Reads {
  /** The workspace has Sense, so "Worth doing next" can point at Today. */
  readonly senseAvailable: boolean;
}

/**
 * The Connect pillar's contents, in consequence order — see rule 2 in the
 * header. Named `ReachDashboard` until slice 65, when "Reach" came to mean
 * paid ads and this stack of email and WhatsApp figures became Connect's.
 *
 * The attention slot is first and renders `null` when nothing has stopped,
 * which is the normal case. A reassurance tile in the most valuable space on the
 * screen, on the 364 days it has nothing to say, is how a reader learns to skip
 * that space on the day it does.
 *
 * **It is mounted over the whole async state, not over `activity.data`.** The
 * first version was `activity.data !== null && <AttentionBlock …>`, so a failed
 * activity read rendered exactly what "nothing has stopped" renders, and a
 * halted campaign vanished with nothing on the page saying a check was missing.
 * `AttentionSlot` says Wend could not check instead — see its header.
 */
function ConnectDashboard({
  overview,
  activity,
  opportunities,
  calendar,
  engagement,
  reach,
  templates,
  senseAvailable,
}: DashboardProps) {
  return (
    <div className="flex flex-col gap-6">
      {/*
        Full width, above the columns. A halted campaign is the one thing on
        this screen that is already costing something, and it must not be shut
        into a 310px rail or pushed under a calendar.
      */}
      <AttentionSlot activity={activity} />

      {/*
        THE TWO-COLUMN SHAPE THE APPROVED DESIGN HAS, AND THIS PAGE DID NOT.
        ---------------------------------------------------------------------
        The mockup is `grid-template-columns: 310px minmax(0,1fr)` above 1000px:
        a narrow left rail holding the calendar and what is coming up, and a
        main column holding everything that reports on sends already made.

        The first build of this slice stacked all of it in one column and called
        the rail "a later layout change". The owner's verdict was that the
        moments were "taking up whole screen which is not kind of good", and
        they were right — a sixty-day list at 950px wide is a different thing
        from the same list at 310px. The rail IS the design, not a refinement of
        it.

        **`xl:`, not `lg:`, and the arithmetic is why.** The mockup is a bare
        1240px page; this app is that page minus a 248px sidebar, which enters
        the flex row at `lg:` (AppShell), minus `--spacing-pad` twice, already
        at its 40px ceiling by then. At exactly 1024px that leaves 696px of
        content, so a fixed 310px rail would leave the MAIN column 366px —
        narrower than the rail beside it, which is worse than one column. At
        1280px content is 952px and the main column 622px, which is the first
        width the grids inside it hold at.

        The rail is a fixed track: the main column, not the rail, absorbs the
        88px by which this app's 1120px ceiling falls short of the mockup's
        1208px. Below the breakpoint the two stack, rail first, as the mockup
        does.
      */}
      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[310px_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-5">
          <CalendarCard calendar={calendar} />
          <MomentsCard calendar={calendar} />
        </div>

        <div className="flex min-w-0 flex-col gap-5">

      {/*
        WHAT TO DO NEXT, ABOVE WHAT ALREADY HAPPENED.

        The owner's judgement on the figures-first version of this page was that
        it "cannot attract marketters or people who are actually seeking
        meaningful information" — accepted, and this is the structural half of
        the answer. Every card below this one reports on sends that have already
        gone out. This is the only one that is about a send that has not.

        It sits **under** `AttentionSlot` and not above it, because a halted
        campaign is the one thing on this screen that is already costing
        something, and a suggestion must never outrank an emergency. It sits
        **over** `SendingHealthTiles` because a bounce rate is a fact to check,
        not a thing to do, and the first question a marketer opens this page with
        is the second kind.

        It renders nothing at all when there is nothing to suggest, so a
        well-run workspace gets the figures back at the top of the page without
        this card ever having to justify its space.
      */}
      <OpportunitiesCard opportunities={opportunities} seeAllOnToday={senseAvailable} />

      {/*
        SLICE 25 MOVED THIS UP, AND IT IS THE SECOND QUESTION ANYBODY ASKS.
        It was the ninth of nine slots — 4.13 viewports and 1,284 words down the
        page, behind four cards explaining why figures were missing. "What did my
        last campaign do" is asked more often than any of those explanations, and
        a card is not read at all if reaching it costs two scrolls.
      */}
      <UnfinishedCampaignsCard campaigns={overview.unfinishedCampaigns} />

      {/*
        THE RATES GET A CARD, WHICH THEY HAVE NEVER HAD.
        ---------------------------------------------------------------------
        Slice 20 split the old "Sending health" card into a header-line plus
        three tiles and dropped the card around them. Everything above and
        below them on this page is a bordered, titled panel; these three sat
        naked between panels, reading as loose furniture rather than an answer
        to a question. The design has them titled and enclosed like everything
        else, and of every gap between this page and that design this was the
        largest single one.

        The wrapper is here and not inside `SendingHealthTiles` on purpose: the
        component has three branches — never sent, sent-but-nothing-reported,
        and measured — and each returns its own shape. Wrapping outside means
        all three get the same chrome, so the section does not appear and
        disappear as a workspace's state changes.

        The title asks the reader's question rather than naming the metric
        family; `sub` carries the channel, which is the thing a reader will
        otherwise supply wrongly. The window sentence inside the component is
        left alone — it names the actual dates, which `sub` cannot.
      */}
      <Card>
        <CardHeader className="p-[18px] pb-3.5">
          {/*
            THE TAG IS NOW THE ONLY CHANNEL STATEMENT ON THIS CARD.
            -----------------------------------------------------------------
            It replaces a sentence that sat above the tiles — "Email only, over
            the last 30 days — 20 Aug 2026 to 19 Sep 2026" — which the owner
            removed because it pushed the tiles out of line with the windowed
            panel beside them. Every fact it carried is here: the channel, the
            window, and the two absolute dates.

            **The channel half is load-bearing and has been deleted once
            before.** Review found it gone in slice 20 and put it back: without
            it a reader supplies "all my messages" themselves, which is the
            denominator confusion behind one of this project's two criticals —
            bounce and complaint computed over every channel while drawn
            against AWS's email-only line. The pressure returns the moment a
            second channel reaches this card's wire, which is exactly what the
            multi-channel work will do.

            Two stacked lines rather than one: `sub` is `shrink-0`, so a single
            forty-character uppercase mono string cannot shrink and the TITLE
            wraps instead at phone width.
          */}
          <CardTitle
            className="text-[0.95rem]"
            sub={
              <span className="flex flex-col items-end gap-0.5 text-right">
                <span data-role="sends-channel">
                  Email · last {overview.window.days} days
                </span>
                <span className="text-[0.62rem] normal-case tracking-normal">
                  {formatDate(overview.window.since)} – {formatDate(overview.window.until)}
                </span>
              </span>
            }
          >
            How your sends are doing
          </CardTitle>
        </CardHeader>
        <CardContent className="p-[18px] pt-0">
          {/*
            THE APPROVED DESIGN'S PERFORMANCE BLOCK: the tiles, the delivery-
            report strip and the projection on the left, and the windowed panel
            in a fixed 250px track on the right.

            The panel is INSIDE this card rather than a card of its own,
            because it answers the same question the title asks and the design
            draws it that way. It was briefly a separate card; a second titled
            box repeating "how are my sends doing" is the furniture this page
            has twice been cut for.

            The rates it draws are slices of the same measurement the tiles
            report, so the two can never disagree about what was measured —
            only about over how many days.
          */}
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_250px]">
            <div className="min-w-0">
              <SendingHealthTiles
                sending={overview.sending}
                window={overview.window}
                deliveryReports={overview.deliveryReports}
                projection={overview.projection}
                engagementSlot={<EngagementTiles engagement={engagement} />}
              />
            </div>
            <RecentWindowPanel activity={activity} sending={overview.sending} />
          </div>
        </CardContent>
      </Card>

      {/*
        THE ONLY CROSS-CHANNEL COMPARISON ON THE PAGE, AND IT SITS HERE ON
        PURPOSE.

        Directly under the sends card, which is email's alone. The reading
        order is "how did the sends go" then "who else is there" — and putting
        the two adjacent is what makes the second one answer the first's
        obvious follow-up.

        It is NOT inside the sends card. That card's whole argument is one
        channel and one contract; a cross-surface panel in it would put the
        comparison in the file whose email-only framing is what keeps the AWS
        thresholds honest.
      */}
      <ReachLedgerCard reach={reach} />

      {/*
        LAST IN THE MAIN COLUMN, where the approved design puts it — four tiles
        across, not a 310px list. It was briefly in the rail; the design has it
        here, and four image swatches need the width.
      */}
      <TemplatesCard templates={templates} />
        </div>
      </div>

      {/*
        SEVEN CARDS CAME OFF THIS PAGE IN SLICE 36, AND NONE OF THEM IS DELETED.
        ---------------------------------------------------------------------
        The workspace owner's instruction was to keep the approved mockup's
        design and nothing else: *"there are some cards at the bottom which are
        not making any sense to me... i will add components when i need them"*.
        The mockup draws a topbar, a rail of two, and a main column of four.
        Everything that used to sit below the grid is gone from the page.

        **Parked, not removed.** `RecentCampaignsCard`, `ActivityVolumeCard`,
        `ActivityOutcomesCard`, `NotDeliveredCard`, `SuppressionCard`,
        `EngagementCard` and `QuickActions` are all still on disk, still
        exported, still covered by their own suites, and each is one line here
        away from coming back. "I will add components when I need them" is a
        park instruction, not a delete one — and two of those files hold
        components the mockup KEEPS (`ActivityCharts.tsx` holds
        `RecentWindowPanel`; `SendingHealthCard.tsx` holds
        `SendingHealthTiles`), so deleting the unwanted halves would be surgery
        around code that is still on screen.

        **What a reader loses, said plainly rather than discovered.** The
        workspace open/click rate and the per-ISP deliverability panel
        (`EngagementCard`) now live only on Analytics. The five reasons a
        message did not arrive (`NotDeliveredCard`) and the suppression totals
        (`SuppressionCard`) have no other home on this screen; both remain
        reachable from their own routes. The two daily charts are the only
        place this product drew a bounce SHAPE over time rather than a single
        rate — that is the biggest real loss here, and it is the owner's to
        weigh.

        **What did NOT go.** `AttentionSlot` stays: it renders nothing at all
        on a healthy workspace, so it is not one of the cards the owner can
        see, and it is this product's only unsolicited notice that a campaign
        stopped itself mid-send. `OnboardingPanel` stays for the same reason —
        it disappears on its own once the steps are done, and it sits above the
        fold rather than at the bottom. `PillarSection` is returned INSTEAD of
        this whole dashboard for a workspace with no Connect entitlement.
      */}

    </div>
  );
}

/**
 * The pillar strip, the dashboard, and the one pillar state that still has to
 * be said.
 *
 * ## FLOW IS OFF THE SCREEN — SLICE 26
 *
 * This once mapped `FEATURE_VALUES` and drew a `PillarSection` per feature, so
 * the Overview ended with cards whose entire content was that the software
 * behind them did not exist yet. Measured: **233 visible words** across the
 * three, of which Sense alone was 175. The owner's instruction was plain:
 * remove them from the UI now, keep them in the concept.
 *
 * **Kept in the concept, exactly.** `FEATURE_VALUES` still holds every
 * feature, `navigationTree` still derives their verdicts, `GET /overview` still
 * returns a row per feature, and `PILLAR_IS_BUILT` still answers for them.
 * Removing any of it would be a live-data hazard rather than a tidy-up:
 * `FEATURE_VALUES` backs the Postgres enum `public.feature`, and Postgres
 * cannot drop an enum value.
 *
 * Sense has been built since then (slices 58-64), and slice 65 brought it back
 * to this page in the only form slice 26's argument allows: a card in
 * `PillarStrip` with a name, a line and a link — no section, no tiles — plus
 * "See all on Today" inside "Worth doing next" for a workspace that has it.
 * Flow is still not built, and is still nowhere on this page.
 *
 * ## ONE PILLAR STATE IS AN HONESTY STATE AND STAYS
 *
 * A tenant **not entitled to Connect** is told so, in the words
 * `navigationTree` and the sidebar use. That is the answer to why a workspace
 * that can reach this page has no dashboard on it, and `AppLayout` says the
 * same thing in the sidebar — Connect is the one pillar group drawn as "Not on
 * this plan" rather than left out, for exactly this reason. Before slice 65
 * the gate read `reach`, which then meant the email and WhatsApp half; the
 * gate followed the meaning, not the word.
 *
 * An entitled workspace sees no pillar heading over the dashboard. One pillar
 * heading under a page the shell has already titled *Overview* says the word
 * twice; the strip above, when it is drawn, already names Connect as a part
 * this workspace has.
 */
function Overview({
  overview,
  activity,
  opportunities,
  calendar,
  engagement,
  reach,
  templates,
}: Reads) {
  /*
   * The verdicts come from the response, which carries one row per feature,
   * through `pillarStateOf` — the strip and the header's actions read the same
   * function, so neither can disagree with this gate. Its fallback for a
   * missing row is the deploy-skew case and is argued in `PillarStrip.tsx`.
   */
  const connectState = pillarStateOf(overview.pillars, 'connect');
  const senseAvailable = pillarStateOf(overview.pillars, 'sense') === 'available';

  if (connectState !== 'available') {
    return (
      <div className="flex flex-col gap-6">
        <PillarStrip pillars={overview.pillars} />
        <PillarSection feature="connect" state={connectState} note={null} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/*
        THE STRIP LEADS, ABOVE THE READ-AT LINE. It is not a figure and has no
        read time of its own — it is the page's table of contents — and a
        reader deciding which pillar to open should not have to get past a
        timestamp to do it. It is a single row of short cards, so it costs the
        fold little.
      */}
      <PillarStrip pillars={overview.pillars} />

      {/*
        SLICE 27 CUT THE SECOND SENTENCE. What stays is the reader's frame — when
        this was read, and over how long. What went was *"the daily charts, and
        opens and clicks, are read separately and carry their own times"*, which
        is a description of the page making three HTTP requests.

        A sender cannot act on which call a figure arrived on, and the sentence
        was insurance against a comparison nobody makes. It is also unnecessary
        on its own terms: every card with its own read already stamps its own
        time, which is the carrier that actually answers the question if somebody
        does ask it.
      */}
      <p className="text-[0.82rem] leading-relaxed text-muted">
        Read at {formatDateTime(overview.generatedAt)}, over the last {overview.window.days} days.
      </p>

      <ConnectDashboard
        overview={overview}
        activity={activity}
        engagement={engagement}
        reach={reach}
        templates={templates}
        opportunities={opportunities}
        calendar={calendar}
        senseAvailable={senseAvailable}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The screen                                                                  */
/* -------------------------------------------------------------------------- */

export function HomePage() {
  const { data, error, status, loadingFirst, reload } = useAsyncData(
    (signal) => fetchOverview(signal),
    [],
  );
  /* The dashboard's gate, asked of the same rows by the same function — see
     "THE HEADER'S ACTIONS ARE CONNECT'S TOO" in the file header. */
  const connectOn = data !== null && pillarStateOf(data.pillars, 'connect') === 'available';

  /*
   * THE OTHER TWO READS, AND WHY NEITHER IS AWAITED WITH THE FIRST.
   *
   * `Promise.all` of the three would be two lines shorter and would hand the
   * whole screen the worst of the three outcomes. The engagement read makes a
   * live SESv2 metrics call against the tenant's own AWS account, and an Amazon
   * timeout would then blank the bounce and complaint rates — the two numbers on
   * this product that can cost somebody their AWS account, and the reason this
   * screen exists at all. The activity read is thirty times the row count of the
   * dashboard read, grouped over a monthly-partitioned table, and it is the part
   * of the screen that can afford to arrive second.
   *
   * So each is its own hook with its own three states, rendered inside its own
   * card. All three start at the same moment rather than in sequence, because
   * none depends on anything in another: they are independent all the way down,
   * which is what lets one of them fail alone.
   */
  const activity = useAsyncData((signal) => fetchOverviewActivity(signal), []);
  /*
   * THE ENGAGEMENT READ IS BACK, AND SLICE 36'S REASONING FOR REMOVING IT WAS
   * PART WRONG.
   *
   * It came off with `EngagementCard`, on the argument that it was that card's
   * only consumer and that it "makes a live SESv2 metrics call against the
   * tenant's own AWS account on every load". Both halves need correcting, and
   * the second is the one that would otherwise argue the next reader into
   * deleting this row again.
   *
   * **The open/click half reaches no provider.** It is two grouped reads over
   * `delivery_events` in the tenant's own database. The expensive half is the
   * per-ISP `isp` breakdown, and `planVdmLookup` returns `not_enabled` before a
   * credential is opened for every workspace that has not paid for VDM — which
   * is nearly all of them.
   *
   * **And the side effect nobody intended:** with this read gone, no workspace
   * could see its own open or click rate anywhere in Wend. `SLICES.md` and this
   * file both said those figures "now live only on Analytics"; they did not.
   *
   * It is threaded ONLY as the sends card's `engagementSlot`, never to anything
   * that draws Accepted, Awaiting or Deliverability, so a slow response cannot
   * blank a figure that came from a different request. The one cost that
   * remains real — a VDM workspace paying for an ISP breakdown this card does
   * not draw — is a parameter the engagement module owns, and is filed rather
   * than bodged here.
   */
  const opportunities = useAsyncData((signal) => fetchOpportunities(signal), []);
  const calendar = useAsyncData((signal) => fetchCalendar(signal), []);
  const engagement = useAsyncData((signal) => fetchEngagement(signal), []);
  const reach = useAsyncData((signal) => fetchReach(signal), []);
  /*
   * The sixth read on this page, and the header's architecture is independent
   * reads rather than a budget of five — each owns its timestamp and its own
   * failure, and none can take another down.
   *
   * It is also the cheapest read here: `pageSize` is four, `templateSummary`
   * carries no blocks and no compiled HTML by construction, and it touches
   * neither AWS nor Meta. It cannot be folded into `GET /overview`, which is
   * one snapshot of figures that may be compared with one another — a list of
   * rows is not a figure, and rule 1 of this header already refused template
   * counts on that response.
   *
   * `type` and `status` are pinned rather than defaulted. Unfiltered, the list
   * returns WhatsApp rows, which have a null subject and cannot start an email
   * campaign in this build; unapproved, it returns drafts the campaign form
   * will not offer, which is the exact gap this card exists to close.
   */
  const templates = useAsyncData(
    (signal) =>
      listTemplates(
        {
          page: 1,
          pageSize: TEMPLATE_ROWS,
          sort: 'updatedAt',
          direction: 'desc',
          type: 'email',
          status: 'approved',
        },
        signal,
      ),
    [],
  );

  return (
    /*
     * CREDITS IS A HEADER CHIP, AND `actions` WAS EMPTY UNTIL SLICE 25.
     *
     * It is the figure a sender checks before every campaign and it was 3.37
     * viewports down the page, below every explanation card. In the header it is
     * on screen whatever else is, which is the only placement that makes
     * done-when 2 true on a short window.
     *
     * Rendered only once the overview read has answered: a chip that guessed
     * while loading would be a figure this page had not been told yet, and the
     * balance is exactly the number nobody should be shown a placeholder for.
     * And only on a plan with Connect, whose figures and verb these all are
     * (slice 65; the file header says why).
     */
    <AppLayout
      title="Overview"
      {...(data === null || !connectOn
        ? {}
        : {
            actions: (
              <>
                <SendabilityChip sendability={data.sendability} />
                <CreditsChip credits={data.credits} />
                {/*
                  THE TWO ACTIONS THE APPROVED DESIGN PUTS IN THE HEADER.

                  A secondary beside the brand-gradient primary, top right,
                  beside the chips — the mockup's arrangement, taken as drawn.

                  "Campaign settings" is the mockup's label and the workspace
                  owner asked for the design without alteration, so it is the
                  label. The router has no settings screen, so it goes to the
                  campaigns list, which is the nearest real destination; if a
                  settings screen is ever built this is the link that should
                  point at it.
                */}
                <Link
                  to="/campaigns"
                  className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                >
                  Campaign settings
                </Link>
                <Link
                  to="/campaigns/new"
                  className={buttonVariants({ variant: 'primary', size: 'sm' })}
                >
                  New campaign
                </Link>
              </>
            ),
          })}
    >
      {/*
       * Above everything, and not inside the `data !== null` branch: a workspace
       * that has connected nothing is exactly the one whose dashboard has least
       * to say, and the checklist must not wait on — or be taken down by — the
       * request that draws it. It supplies its own spacing here because it is
       * frequently absent, and a gap left by a component that rendered nothing
       * is the layout's problem rather than the component's.
       *
       * Unconditional on purpose, and it is not a missing entitlement check: the
       * panel reads the session and draws nothing for a workspace without Connect
       * — `onboarding-entitlement.ts` — so the gate travels with it. Slice 20
       * decided where this sits and decided it sits here: this is where a new
       * tenant lands, and `QuickActions` below is for a workspace that is
       * already going.
       */}
      <OnboardingPanel className="mb-6" />

      {loadingFirst && (
        <Card>
          <CardContent className="flex items-center gap-3 px-6 py-14 text-[0.9rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Loading your overview…</span>
          </CardContent>
        </Card>
      )}

      {status === 'error' && data === null && (
        <Card>
          <ErrorState
            error={error}
            title="The overview did not load"
            onRetry={reload}
            className="flex flex-col items-start gap-3 px-6 py-10"
          />
        </Card>
      )}

      {data !== null && (
        <div className="flex flex-col gap-6">
          {status === 'error' && (
            <ErrorState
              error={error}
              title="Could not refresh"
              onRetry={reload}
              className="flex flex-col items-start gap-3 rounded-base border border-line px-5 py-4"
            />
          )}
          <Overview
            overview={data}
            activity={activity}
            opportunities={opportunities}
            calendar={calendar}
            engagement={engagement}
            reach={reach}
            templates={templates}
          />
        </div>
      )}
    </AppLayout>
  );
}
